/**
 * MyJobs Zustand Store State Types
 *
 * This file contains the internal state structure for the MyJobs Zustand store.
 * These types define the shape of the store's state tree.
 *
 * DO NOT import these directly in components - use the store via @worker/stores
 * For domain/API types, see @worker/types/myJobs.type
 */
import {
  ViewsTypesEnum,
  MyJobsTabsEnum,
  JobRecommendationsPreferences,
} from '@worker/types/myJobs.type';
import { UserRole } from '@constants/userRoles';
import { IWorkerData, Skill } from '@th-types/worker.type';
import {
  Comment,
  Education,
  IWorkerEndorsementsData,
  NonPromoWorkExperience,
  PromoWorkExperience,
} from '@th-types/workerProfile.type';
import Picture from '@th-types/picture.type';
import { ICertificatesData } from '@th-types/certificates.type';
import { WorkerProfileAllowedOptions } from '@worker/types/worker-profile-types.type';
import { CountryName } from '@th-types/address.type';

// Search & Filter Types
export type SearchFilters = {
  readonly companyName: string;
  readonly companyId: string | null;
  readonly address: google.maps.places.PlaceResult | null;
  readonly miles: number | null;
};

export type PaginationState = Readonly<{
  currentPage: number;
  itemsPerPage: number;
}>;

export type JobLocationFilters = Readonly<{
  country: '' | CountryName;
  includeNational: boolean;
}>;

export type RateFilters = {
  readonly minHourlyEnabled: boolean;
  readonly minHourlyRate?: number;
  readonly minDailyEnabled: boolean;
  readonly minDailyRate?: number;
};

export type AdvancedFiltersFormValues = Readonly<{
  startDate: Date | null;
  endDate: Date | null;
  onGoing: boolean;
  jobLocation: JobLocationFilters;
  jobTitles: string[];
}> &
  RateFilters;

export const DEFAULT_ADVANCED_FILTERS: AdvancedFiltersFormValues = {
  startDate: null,
  endDate: null,
  onGoing: false,
  jobLocation: {
    country: '',
    includeNational: true,
  },
  jobTitles: [],
  minHourlyEnabled: false,
  minHourlyRate: undefined,
  minDailyEnabled: false,
  minDailyRate: undefined,
} as const;

// User Types
export type UserState = Readonly<{
  loggedIn: boolean;
  id: number;
  name: string;
  email: string | null;
  profileImageUrl: string;
  roles: readonly UserRole[];
}>;

export const DEFAULT_USER_STATE: UserState = {
  loggedIn: false,
  id: 0,
  name: '',
  email: null,
  profileImageUrl: '',
  roles: [],
} as const;

// Worker Profile Types
type WorkerProfileContent = {
  readonly workerProfile?: IWorkerData;
  readonly workerProfileAllowedOptions?: WorkerProfileAllowedOptions;
  readonly nonPromoWorkExperience?: readonly NonPromoWorkExperience[];
  readonly promoWorkExperience?: readonly PromoWorkExperience[];
  readonly workerEducation?: readonly Education[];
  readonly workerEndorsements?: IWorkerEndorsementsData;
  readonly workerMediaFiles?: readonly Picture[];
  readonly workerReviews?: readonly Comment[];
  readonly workerSkills?: readonly Skill[];
  readonly workerCertificates?: ICertificatesData;
  readonly jobPreferences?: JobRecommendationsPreferences;
};

type WorkerProfileFlags = {
  readonly surveysEnabled: boolean;
  readonly isLoadingWorkerProfile: boolean;
  readonly isLoadingWorkerOptions: boolean;
};

export type WorkerProfileState = WorkerProfileContent & WorkerProfileFlags;

export const DEFAULT_WORKER_PROFILE_STATE: WorkerProfileState = {
  workerProfile: undefined,
  workerProfileAllowedOptions: undefined,
  nonPromoWorkExperience: undefined,
  promoWorkExperience: undefined,
  workerEducation: undefined,
  workerEndorsements: undefined,
  workerMediaFiles: undefined,
  workerReviews: undefined,
  workerSkills: undefined,
  workerCertificates: undefined,
  jobPreferences: undefined,
  surveysEnabled: false,
  isLoadingWorkerProfile: false,
  isLoadingWorkerOptions: false,
} as const;

// UI State Types
type UIState = {
  readonly showAdvancedFilters: boolean;
  readonly showConfigureModal: boolean;
  readonly showGetInNetworkModal: boolean;
  readonly currentView: ViewsTypesEnum;
  readonly currentTab: MyJobsTabsEnum;
};

// Filter State Types
type FilterState = {
  readonly searchFilters: SearchFilters;
  readonly advancedFilters: AdvancedFiltersFormValues;
  readonly pagination: PaginationState;
};

// Complete MyJobs State
export type MyJobsState = UIState &
  FilterState & {
    readonly user: UserState | null;
    readonly showMobileFilters: boolean;
    readonly isLoading: boolean;
    readonly workerProfile: WorkerProfileState;
  };
